import * as React from "react";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

import { type ShortcutKey, useKeyboardShortcut } from "./use-keyboard-shortcut";
import { useIsMobile } from "./use-mobile";

const DETAILS_PANE_STORAGE_KEY = "istari-details-pane-state";

export const DETAILS_PANE_KEYBOARD_SHORTCUT: ShortcutKey[] = ["meta", "]"];
export const DETAILS_PANE_DEFAULT_SIZE = 25;
export const DETAILS_PANE_MIN_SIZE = 15;
export const DETAILS_PANE_MAX_SIZE = 40;

function getStorageKeyName(storageKey?: string): string {
  return storageKey ? `${DETAILS_PANE_STORAGE_KEY}_${storageKey}` : DETAILS_PANE_STORAGE_KEY;
}

function getInitialOpenState(defaultOpen: boolean, storageKey?: string): boolean {
  const storedValue = localStorage.getItem(getStorageKeyName(storageKey));
  return storedValue !== null ? storedValue === "true" : defaultOpen;
}

type DetailsPaneContextProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  togglePane: () => void;
  open: () => void;
  isMobile: boolean;
  panelRef: React.RefObject<ImperativePanelHandle | null>;
  storageKey?: string;
  onPanelCollapse: () => void;
  onPanelExpand: () => void;
};

type DetailsPaneActionsContextProps = {
  open: () => void;
};

const DetailsPaneContext = React.createContext<DetailsPaneContextProps | null>(null);
const DetailsPaneActionsContext = React.createContext<DetailsPaneActionsContextProps | null>(null);

export function useDetailsPane() {
  const context = useContext(DetailsPaneContext);
  if (!context) {
    throw new Error("useDetailsPane must be used within a DetailsPaneProvider.");
  }
  return context;
}

export function useDetailsPaneOptional() {
  return useContext(DetailsPaneContext);
}

export function useDetailsPaneActionsOptional() {
  return useContext(DetailsPaneActionsContext);
}

interface DetailsPaneProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
}

export function DetailsPaneProvider({ children, defaultOpen = true, storageKey }: DetailsPaneProviderProps) {
  const isMobile = useIsMobile();
  const panelRef = useRef<ImperativePanelHandle>(null);

  const [isOpen, setIsOpenInternal] = useState(() => getInitialOpenState(defaultOpen, storageKey));

  const storageKeyName = getStorageKeyName(storageKey);

  const onPanelCollapse = useCallback(() => {
    setIsOpenInternal(false);
    localStorage.setItem(storageKeyName, String(false));
  }, [storageKeyName]);

  const onPanelExpand = useCallback(() => {
    setIsOpenInternal(true);
    localStorage.setItem(storageKeyName, String(true));
  }, [storageKeyName]);

  const setIsOpen = useCallback(
    (open: boolean) => {
      setIsOpenInternal(open);
      localStorage.setItem(storageKeyName, String(open));
      if (!isMobile) {
        if (open) {
          panelRef.current?.expand();
        } else {
          panelRef.current?.collapse();
        }
      }
    },
    [storageKeyName, isMobile],
  );

  const togglePane = useCallback(() => {
    setIsOpenInternal((prev) => {
      const newValue = !prev;
      localStorage.setItem(storageKeyName, String(newValue));
      if (!isMobile) {
        if (newValue) {
          panelRef.current?.expand();
        } else {
          panelRef.current?.collapse();
        }
      }
      return newValue;
    });
  }, [storageKeyName, isMobile]);

  const open = useCallback(() => {
    const storedValue = localStorage.getItem(storageKeyName);
    if (storedValue === "false") {
      return;
    }
    setIsOpen(true);
  }, [storageKeyName, setIsOpen]);

  useKeyboardShortcut(DETAILS_PANE_KEYBOARD_SHORTCUT, togglePane);

  const contextValue = useMemo<DetailsPaneContextProps>(
    () => ({
      isOpen,
      setIsOpen,
      togglePane,
      open,
      isMobile,
      panelRef,
      storageKey,
      onPanelCollapse,
      onPanelExpand,
    }),
    [isOpen, setIsOpen, togglePane, open, isMobile, storageKey, onPanelCollapse, onPanelExpand],
  );

  // Rows only need the command, not the open/closed state; split the action context to avoid
  // re-rendering large file trees when the details pane toggles.
  const actionsContextValue = useMemo<DetailsPaneActionsContextProps>(
    () => ({
      open,
    }),
    [open],
  );

  return (
    <DetailsPaneActionsContext.Provider value={actionsContextValue}>
      <DetailsPaneContext.Provider value={contextValue}>{children}</DetailsPaneContext.Provider>
    </DetailsPaneActionsContext.Provider>
  );
}
